import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { DYNAMIC_CLOUD_CUSTOMIZATION } from './app.injectiontoken';
import { AuthGuard } from './components/auth/auth.guard';
import { ContextChangeComponent } from './components/context-change/context-change.component';
import { MaintenanceComponent } from './components/maintenance/maintenance.component';
import { FeatureFlagService } from './components/my-profile/services/feature-flag.service';
import { NotificationComponent } from './components/services/notifications/list-notifications/list-notifications.component';
import { CreateSessionFeedbackComponent } from './shared/dialogs/create-session-feedback/create-session-feedback.component';

const routes: Routes = [
	{
		path: '',
		pathMatch: 'full',
		redirectTo: '/dashboard',
	},
	{
		path: 'maintenance',
		component: MaintenanceComponent,
	},
	{
		path: 'changeCtx',
		component: ContextChangeComponent,
	},
	{
		path: 'sessionFeeback',
		component: CreateSessionFeedbackComponent,
	},
	{
		path: 'account-management',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/account-management/account-management.module').then((m) => m.AccountManagementModule),
	},
	{
		path: 'my-profile',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/my-profile/myprofile.module').then((m) => m.MyProfileModule),
	},
	{
		path: 'audit-log',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/audit-log/audit-log.module').then((m) => m.AuditLogModule),
	},
	{
		path: 'az-fundamentals',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/azure-fundamentals/azure-fundamentals.module').then((m) => m.AzureFundamentalsModule),
	},
	{
		path: 'car-rental',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/car-rental/car-rental.module').then((m) => m.CarRentalModule),
	},
	{
		path: 'cmsmgw',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/cmsmgw/cmsmgw.module').then((m) => m.CmsMgwModule),
	},
	{
		path: 'container-registry',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/container-registry/container-registry.module').then((m) => m.ContainerRegistryModule),
	},
	{
		path: 'corc',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/corc/corc.module').then((m) => m.CorcModule),
	},
	{
		path: 'customer-db',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/customer-db/customer-db.module').then((m) => m.CustomerDbModule),
	},
	{
		path: 'dashboard',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/dashboard/dashboard.module').then((m) => m.DashboardModule),
	},
	{
		path: 'dci-ng',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/dci-ng/dci-ng.module').then((m) => m.DciNgModule),
	},
	{
		path: 'domains-dns',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/domains-dns/domains-dns.module').then((m) => m.DomainsDnsModule),
	},
	{
		path: 'drive',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/drive/drive.module').then((m) => m.DriveModule),
	},
	{
		path: 'dynamic-cloud',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/dynamic-cloud/dynamic-cloud.module').then((m) => m.DynamicCloudModule),
		providers: [
			{
				provide: DYNAMIC_CLOUD_CUSTOMIZATION,
				useValue: {
					displayName: 'Dynamic Cloud',
					name: 'dynamic-cloud',
				},
			},
		],
	},
	{
		path: 'viridium-cloud',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/dynamic-cloud/dynamic-cloud.module').then((m) => m.DynamicCloudModule),
		providers: [
			{
				provide: DYNAMIC_CLOUD_CUSTOMIZATION,
				useValue: {
					displayName: 'Viridium Cloud',
					name: 'viridium-cloud',
				},
			},
		],
	},
	{
		path: 'database-service',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/database-service/database.module').then((m) => m.DatabaseModule),
	},
	{
		path: 'event-bridge',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/event-bridge/event-bridge.module').then((m) => m.EventBridgeModule),
	},
	{
		path: 'generic-services',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/generic-services/generic-services.module').then((m) => m.GenericServicesModule),
	},
	{
		path: 'iam',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/iam/iam.module').then((m) => m.IamModule),
	},
	{
		path: 'ip-management',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/ip-manangement/ip-manangement.module').then((m) => m.IpManangementModule),
	},
	{
		path: 'managed-gateway',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/managed-gateway/managed-gateway.module').then((m) => m.ManagedGatewayModule),
	},
	{
		path: 'managed-windows',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/managed-windows/managed-windows.module').then((m) => m.ManagedWindowsModule),
	},
	{
		path: 'metering-engine',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/metering-engine/metering-engine.module').then((m) => m.MeteringEngineModule),
	},
	{
		path: 'mia-museum',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/mia-museum/mia-museum.module').then((m) => m.MiaMuseumModule),
	},
	{
		path: 'order-processing',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/order-processing/order-processing.module').then((m) => m.OrderProcessingModule),
	},
	{
		path: 'report-creator',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/report-creator/report-creator.module').then((m) => m.ReportCreatorModule),
	},
	{
		path: 'service-registry',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/service-registry/service-registry.module').then((m) => m.ServiceRegistryModule),
	},
	{
		path: 'sms-notifications',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/sms-notifications/sms-notifications.module').then((m) => m.SmsNotifyModule),
	},
	{
		path: 'tos-manager',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/tos-manager/tos-manager.module').then((m) => m.TosManagerModule),
	},
	{
		path: 'ssl-monitoring',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/ssl-monitoring/ssl-monitoring.module').then((m) => m.SslMonitoringModule),
	},
	{
		path: 't-rex',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/resource-explorer/resource-explorer.module').then((m) => m.ResourceExplorerModule),
	},
	{
		path: 'vpc',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/vpc/vpc.module').then((m) => m.VpcModule),
	},
	{
		path: 'terraform-management',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/terraform-management/terraform-management.module').then((m) => m.TerraformManagementModule),
	},
	{
		path: 'simple-vpn',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/simple-vpn/simple-vpn.module').then((m) => m.SimpleVpmModule),
	},
	{
		path: 's3',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/s3/s3.module').then((m) => m.S3Module),
	},
	{
		path: 'rotation-calendar',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/rotation-calendar/rotation-calendar.module').then((m) => m.RotationCalendarModule),
	},
	{
		path: 'pricing-calculator',
		canActivate: [
			AuthGuard,
			() => {
				const featureFlagService = inject(FeatureFlagService);
				return featureFlagService.getFeatureFlagState('pricingCalculator');
			},
		],
		loadChildren: () =>
			import('./components/services/pricing-calculator/pricing-calculator.module').then((m) => m.PricingCalculatorModule),
	},
	{
		path: 'monitoring-hub',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/monitoring-hub/monitoring-hub.module').then((m) => m.MonitoringHubModule),
	},
	{
		path: 'managed-server',
		canActivate: [AuthGuard],
		loadChildren: () => import('./components/services/managed-server/managed-os.module').then((m) => m.ManagedOsModule),
	},
	{
		path: 'managed-environment',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/managed-environment/managed-environment.module').then((m) => m.ManagedEnvironmentModule),
	},
	{
		path: 'managed-env',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/managed-environment/managed-environment.module').then((m) => m.ManagedEnvironmentModule),
	},
	{
		path: 'notifications',
		canActivate: [AuthGuard],
		component: NotificationComponent,
	},
	{
		path: 'firewall-management',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./components/services/firewall-management/firewall-management.module').then((m) => m.FirewallManagementModule),
	},
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule],
})
export class AppRoutingModule {}
